import React, { useEffect, useRef, useState, useCallback } from 'react';
// react-window removed due to runtime mismatch; using mapped list with intersection sentinel
import axios from 'axios';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Alert from '@mui/material/Alert';
import useMediaQuery from '@mui/material/useMediaQuery';
import { JobViewStyled, RuleStyled, OverflowContainerStyled, ImageAltStyled } from '../../../styled/JobConnection';
import { useTheme } from '../../UI/Theme/ThemeContext';
import { FaCheckCircle, FaTimesCircle, FaEnvelope, FaRegCopy, FaClipboardList, FaCalendarAlt, FaEye } from 'react-icons/fa';
import AuthHeader from '../../UI/AuthHeader/AuthHeader';
import AuthSideMenu from '../../UI/AuthSideMenu/AuthSideMenu';
import Button from '../../UI/Buttons/Button';
import { ButtonSubmitGreen } from '../../UI/Buttons/Buttons';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { FaLongArrowAltLeft } from 'react-icons/fa';
// import { useNavigate } from 'react-router-dom';
import { useConfirm } from 'material-ui-confirm';
import { PlainModalOverlay } from '../../UI/Modal/Modal';
import AuthInput from '../../UI/Input/AuthInputs';
import OvalSpinner from '../../UI/Spinners/OvalSpinner';
import { SiGmail } from 'react-icons/si';
import StartPillButton from '../../UI/Buttons/StartPillButton';
import { useErrorSetter, useSuccessSetter } from '../../../hooks/useUiMessages';
import { copyText, parseAndValidateEmails, startAutoApply, checkAuthenticatedUser } from '../../../utils/client-functions';
import PageHeaderStyled from '../../../styled/PageHeader';

const CompletedApplications = () => {
  const { themeName } = useTheme();
  const isDark = themeName === 'dark';
  const isDesktop = useMediaQuery('(min-width:992px)');
  const headerRef = useRef(null);
  const [headerPad, setHeaderPad] = useState(12);
  const [items, setItems] = useState([]);
  const [totalCount, setTotalCount] = useState(null);
  const [totalThisMonth, setTotalThisMonth] = useState(null);
  const [localLoading, setLocalLoading] = useState(true);
  const MIN_SPINNER_MS = 400;
  const [authMenuOpen, setAuthMenuOpen] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const toggleMenu = () => setAuthMenuOpen(v => !v);
  const confirm = useConfirm();
  const errorSetter = useErrorSetter();
  const successSetter = useSuccessSetter();

  // Gmail integration chip state (mirrors JobHub)
  const [gmailGranted, setGmailGranted] = useState(false);
  const [gmailIsGmail, setGmailIsGmail] = useState(false);
  const [disconnectPending, setDisconnectPending] = useState(false);
  const [gmailAuthUrl, setGmailAuthUrl] = useState('');
  const [showEmailPermModal, setShowEmailPermModal] = useState(false);
  const [pendingGmailJobObj, setPendingGmailJobObj] = useState(null);

  // Retry flow state
  const [retryingJob, setRetryingJob] = useState(null); // jobId of active retry
  const [retryBatchId, setRetryBatchId] = useState('');
  const [progressText, setProgressText] = useState('Starting...');
  const [progressCounts, setProgressCounts] = useState({ done: 0, total: 0 });
  // UI states for Gmail connect flow
  const [connectingJob, setConnectingJob] = useState(null); // jobId currently in Gmail connect phase
  const [gmailJustConnectedJob, setGmailJustConnectedJob] = useState(null); // brief label when gmail connected
  const gmailPopupRef = useRef(null);
  const gmailPopupCheckRef = useRef(null);


  // Manual email modal for 'no_company_email_found'
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [emailInput, setEmailInput] = useState('');
  const [emailForJob, setEmailForJob] = useState(null); // { jobId, reason, cardKey }

  // Refs for FLIP animation when moving a card to top
  const itemRefs = useRef({});
  const pendingFlip = useRef(null); // stores prev positions map for next paint
  const scrollTopPending = useRef(false);
  const bottomRef = useRef(null);

  // key generator for items (stable across reorders)
  const keyForItem = (it, idx) => String(it?._id || it?.jobKey || it?.jobId || it?.job_id || it?.id || idx);

  // Limit displayed items for performance / UX
  const PAGE_SIZE = 50;
  const [skip, setSkip] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  // Helpers to animate reordering (FLIP)
  const capturePositions = () => {
    const map = {};
    try {
      Object.keys(itemRefs.current).forEach(k => {
        const el = itemRefs.current[k];
        if (el && el.getBoundingClientRect) {
          map[k] = el.getBoundingClientRect().top;
        }
      });
    } catch {}
    return map;
  };
  const playFlip = (prev) => {
    if (!prev) return;
    try {
      requestAnimationFrame(() => {
        Object.keys(prev).forEach(k => {
          const el = itemRefs.current[k];
          if (!el || !el.getBoundingClientRect) return;
          const nowTop = el.getBoundingClientRect().top;
          const dy = prev[k] - nowTop;
          if (Math.abs(dy) > 1) {
            el.style.transition = 'none';
            el.style.transform = `translateY(${dy}px)`;
            // force reflow
            void el.offsetHeight;
            // slower transition so the movement is clearly visible
            el.style.transition = 'transform 1000ms cubic-bezier(.2,.8,.2,1)';
            el.style.transform = 'translateY(0)';
            const cleanup = () => {
              el.style.transition = '';
              el.style.transform = '';
              el.removeEventListener('transitionend', cleanup);
            };
            el.addEventListener('transitionend', cleanup);
          }
        });
      });
    } catch {}
  };
  const moveCardToTopAnimated = (cardKey) => {
    try {
      const id = String(cardKey || '');
      if (!id) return;
      const prev = capturePositions();
      // schedule flip after DOM updates
      pendingFlip.current = prev;
      setItems(curr => {
        const arr = Array.isArray(curr) ? [...curr] : [];
        const idx = arr.findIndex((it, i) => keyForItem(it, i) === id);
        if (idx <= 0) return curr; // already at top or not found
        const [moved] = arr.splice(idx, 1);
        return [moved, ...arr];
      });
      // Defer scroll until after FLIP kicks in to avoid breaking measurements
      scrollTopPending.current = true;
    } catch {}
  };

  // After items reorder, run the FLIP animation if pending
  useEffect(() => {
    // Listen for postMessage from Gmail popup
    const msgHandler = (ev) => {
      try {
        if (ev && ev.data && ev.data.type === 'gmail_connected') {
          setGmailGranted(true);
          setGmailIsGmail(true);
          try { successSetter('Gmail connected'); } catch(_) {}
          try { if (gmailPopupCheckRef.current) { clearInterval(gmailPopupCheckRef.current); gmailPopupCheckRef.current = null; } } catch(_) {}
          try { gmailPopupRef.current = null; } catch(_) {}
        }
      } catch(_) {}
    };
    const customHandler = () => {
      try {
        setGmailGranted(true);
        setGmailIsGmail(true);
        try { successSetter('Gmail connected'); } catch(_) {}
        try { if (gmailPopupCheckRef.current) { clearInterval(gmailPopupCheckRef.current); gmailPopupCheckRef.current = null; } } catch(_) {}
        try { gmailPopupRef.current = null; } catch(_) {}
      } catch(_) {}
    };
    try { window.addEventListener('message', msgHandler); } catch(_) {}
    try { window.addEventListener('gmail_connected', customHandler); } catch(_) {}
    return () => { try { window.removeEventListener('message', msgHandler); } catch(_) {} try { window.removeEventListener('gmail_connected', customHandler); } catch(_) {} };
  }, [successSetter]);

  useEffect(() => {
    return () => {
      try { if (gmailPopupCheckRef.current) { clearInterval(gmailPopupCheckRef.current); gmailPopupCheckRef.current = null; } } catch(_) {}
      try { gmailPopupRef.current = null; } catch(_) {}
    };
  }, []);

  useEffect(() => {
    if (pendingFlip.current) {
      const prev = pendingFlip.current;
      pendingFlip.current = null;
      playFlip(prev);
      // purge stale item refs not present anymore
      try {
        const present = new Set(items.map((it, i) => keyForItem(it, i)));
        Object.keys(itemRefs.current).forEach(k => { if (!present.has(k)) delete itemRefs.current[k]; });
      } catch {}
      // Smooth scroll to top slightly after animation starts
      if (scrollTopPending.current) {
        scrollTopPending.current = false;
        setTimeout(() => { try { window.scrollTo({ top: 0, behavior: 'smooth' }); } catch {} }, 20);
      }
    }
  }, [items]);

  useEffect(() => {
    const updatePad = () => {
      try {
        const h = headerRef.current ? headerRef.current.offsetHeight : 0;
        const baseMin = isDesktop ? 12 : 6;
        const extra = isDesktop ? 10 : 6;
        // reduce the large desktop offset to tighten top spacing
        const desktopExtraOffset = isDesktop ? 12 : 0;
        const mobileAdjustment = isDesktop ? 0 : -12;
        setHeaderPad(Math.max(baseMin, h + extra + desktopExtraOffset + mobileAdjustment));
      } catch(_) {}
    };
    updatePad();
    window.addEventListener('resize', updatePad);
    return () => window.removeEventListener('resize', updatePad);
  }, [isDesktop]);

  // Function to fetch Gmail permission status and auth URL (invoked after completed-applications loads)
  const fetchGmailStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const r = await axios.get('/user/gmail/permission-status', { headers: { 'x-access-token': token } });
      setGmailIsGmail(!!r?.data?.isGmail);
      setGmailGranted(!!r?.data?.granted);
      if (r?.data?.isGmail && !r?.data?.granted) {
        try {
          const s = await axios.get('/user/gmail/oauth/start?mode=popup', { headers: { 'x-access-token': token }});
          if (s?.data?.authUrl) setGmailAuthUrl(s.data.authUrl);
        } catch {}
      }
    } catch {}
  };

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        await checkAuthenticatedUser();
      } catch (err) {
        try { navigate('/popin?auto-apply'); } catch(_) {}
        return;
      }
      const start = Date.now();
      try {
        setLocalLoading(true);
        const token = localStorage.getItem('token');
        // fetch only the first page for faster initial paint
        const resp = await axios.get(`/user/completed-applications?limit=${PAGE_SIZE}&skip=0`, { headers: { 'x-access-token': token } });
        if (mounted) {
          const nextItems = Array.isArray(resp.data?.items) ? resp.data.items : [];
          setItems(nextItems);
          setSkip(nextItems.length);
          // If the returned page is full and total indicates more, allow Show more
          const total = typeof resp.data?.total === 'number' ? resp.data.total : null;
          setHasMore(nextItems.length === PAGE_SIZE && total !== null && total > nextItems.length);
          setTotalCount(typeof resp.data?.total === 'number' ? resp.data.total : nextItems.length);
          setTotalThisMonth(typeof resp.data?.totalThisMonth === 'number' ? resp.data.totalThisMonth : null);
          // After the completed-applications list has been populated, fetch Gmail permission status in background
          try { fetchGmailStatus(); } catch(_) {}
        }
      } catch (e) {
        // ignore, show empty
        errorSetter('Failed to load applications. Try refreshing the page.');
      } finally {
          // enforce minimum spinner time and wait for two RAFs to ensure paint
          try {
            const elapsed = Date.now() - start;
            const waitMs = Math.max(0, MIN_SPINNER_MS - elapsed);
            if (waitMs) await new Promise(res => setTimeout(res, waitMs));
            // wait two animation frames for the DOM to settle and paint
            await new Promise(res => requestAnimationFrame(() => res()));
            await new Promise(res => requestAnimationFrame(() => res()));
          } catch (_) {}
          setLocalLoading(false);
      }
    };
    load();
    return () => { mounted = false; };
  }, [dispatch, navigate]);

  const loadMore = useCallback(async () => {
    if (loadingMore) return;
    setLoadingMore(true);
    try {
      const token = localStorage.getItem('token');
      const resp = await axios.get(`/user/completed-applications?limit=${PAGE_SIZE}&skip=${skip}`, { headers: { 'x-access-token': token } });
      const nextItems = Array.isArray(resp.data?.items) ? resp.data.items : [];
      const total = typeof resp.data?.total === 'number' ? resp.data.total : null;
      setItems(prev => {
        const combined = [...prev, ...nextItems];
        const totalCountVal = total !== null ? total : combined.length;
        setTotalCount(totalCountVal);
        setHasMore(nextItems.length === PAGE_SIZE && total !== null && combined.length < total);
        return combined;
      });
      setSkip(prev => prev + nextItems.length);
    } catch (err) {
      // ignore
    } finally {
      setLoadingMore(false);
    }
  }, [loadingMore, PAGE_SIZE, skip]);

  // IntersectionObserver sentinel for infinite-scroll
  useEffect(() => {
    if (!hasMore) return;
    const el = bottomRef.current;
    if (!el) return;
    let obs = null;
    try {
      obs = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting && !loadingMore && !localLoading) loadMore();
        });
      }, { root: null, rootMargin: '300px', threshold: 0.1 });
      obs.observe(el);
    } catch (err) {}
    return () => { try { if (obs) obs.disconnect(); } catch(_) {} };
  }, [hasMore, loadingMore, localLoading, loadMore]);

  


  // SSE for progress of the active retry batch
  useEffect(() => {
    if (!retryingJob) return;
    const token = localStorage.getItem('token');
    const src = new EventSource(`/user/progress${token ? `?token=${encodeURIComponent(token)}` : ''}`);
    const onMsg = (event) => {
      try {
        const payload = JSON.parse(event.data);
        if (!payload) return;
        if (payload.type === 'autoApplyStarted' && retryBatchId) {
          setProgressCounts({ done: 0, total: payload.total || 1 });
          setProgressText('Starting...');
        }
        if ((payload.type === 'autoApplyProgress' || payload.type === 'autoApplyUpdate') && payload.batchId === retryBatchId) {
          setProgressCounts(prev => ({
            done: typeof payload.done === 'number' ? payload.done : prev.done,
            total: typeof payload.total === 'number' ? payload.total : (prev.total || payload.total)
          }));
          if (payload.stage === 'resume_optimize') setProgressText('Optimizing resume');
          else if (payload.stage === 'cover_letter') setProgressText('Generating cover letter');
          else if (payload.stage === 'sending_email' || payload.stage === 'sent') setProgressText('Sending application');
          else if (payload.stage === 'email_discovery') setProgressText('Discovering email');
          else if (payload.stage === 'email_manual') setProgressText('Using provided email');
          else setProgressText('Processing...');
        }
        if (payload.type === 'autoApplyDone' && (!retryBatchId || payload.batchId === retryBatchId)) {
          setProgressText('Completed');
          const jobIdForToast = retryingJob; // capture current job id for toast decision
          setTimeout(() => {
            setRetryingJob(null);
            setRetryBatchId('');
            // refresh list
            (async () => {
              try {
                const tk = localStorage.getItem('token');
                  console.debug('[CompletedApplications] autoApplyDone: fetching list', { batchId: payload.batchId, jobIdForToast });
                  const resp = await axios.get('/user/completed-applications', { headers: { 'x-access-token': tk } });
                  const nextItems = Array.isArray(resp.data?.items) ? resp.data.items : [];
                  setItems(nextItems);
                  setTotalCount(typeof resp.data?.total === 'number' ? resp.data.total : nextItems.length);
                // Emit success/error toast for the retried job based on latest status
                try {
                  const match = nextItems.find(it => String(it?.jobId || it?.job_id || it?.id || '') === String(jobIdForToast || ''));
                  if (match) {
                    if (match.status === 'success') {
                      successSetter('Application sent');
                      try { window.dispatchEvent(new CustomEvent('autoApply:jobUpdated', { detail: { jobId: String(jobIdForToast || '') } })); } catch(_) {}
                    } else if (match.status === 'failed') {
                      const msg = match.reason ? `Retry failed: ${match.reason}` : 'Retry failed';
                      errorSetter(msg);
                    }
                  }
                } catch {}
              } catch {}
            })();
          }, 600);
        }
      } catch {}
    };
    src.onmessage = onMsg;
    return () => { try { src.close(); } catch{} };
  }, [retryingJob, retryBatchId, successSetter, errorSetter]);

  const handleDisconnectGmail = async () => {
    try {
      setDisconnectPending(true);
      const token = localStorage.getItem('token');
      await axios.post('/user/gmail/disconnect', {}, { headers: { 'x-access-token': token } });
      setGmailGranted(false);
    } catch {}
    setDisconnectPending(false);
  };

  // Start a single-job auto-apply, optionally with manual email
  const startRetry = async (jobId, manualEmailArray = null, forceSend = false) => {
    try {
      setRetryingJob(jobId);
      setProgressText('Starting...');
      setProgressCounts({ done: 0, total: 1 });
      const manualMap = {};
      if (manualEmailArray) manualMap[jobId] = Array.isArray(manualEmailArray) ? manualEmailArray : [String(manualEmailArray).trim()];
      const respData = await startAutoApply({ selectedJobIds: [jobId], manualEmailsMap: Object.keys(manualMap).length ? manualMap : undefined, forceSend });
      if (respData?.batchId) setRetryBatchId(respData.batchId);
    } catch (e) {
      // quota or pricing open flow
      const resp = e?.response;
      if (resp && (resp.data?.status === 'quota_exceeded' || (resp.status === 403 && resp.data?.openPricing))) {
        try { window.dispatchEvent(new CustomEvent('openResumePricing')); } catch(_) {}
      }
      setRetryingJob(null);
    }
  };

  const ensureGmailThenStart = async (jobObj, manualEmail) => {
    const isAuth = localStorage.getItem('token');
    try {
      // Prefer client-side state: avoid querying permission-status if we already fetched it on page load
      if (typeof gmailIsGmail === 'boolean') {
        if (gmailIsGmail && !gmailGranted) {
          // show the email-permission modal and defer the popup until user confirms
          try { setPendingGmailJobObj(jobObj); } catch(_) {}
          setShowEmailPermModal(true);
          return;
        }
      }
      // Fallback: permission-state unknown — query server as before
      const r = await axios.get('/user/gmail/permission-status', { headers: { 'x-access-token': isAuth }, timeout: 300000 });
      const { isGmail, granted } = r.data || {};
      if (isGmail && !granted) {
        setGmailIsGmail(true);
        setGmailGranted(false);
        if (!gmailAuthUrl) {
          try {
            const s = await axios.get('/user/gmail/oauth/start?mode=popup', { headers: { 'x-access-token': isAuth }});
            if (s?.data?.authUrl) setGmailAuthUrl(s.data.authUrl);
          } catch {}
        }
        if (gmailAuthUrl) {
          const w = 520, h = 640;
          const y = window.top.outerHeight / 2 + window.top.screenY - ( h / 2);
          const x = window.top.outerWidth / 2 + window.top.screenX - ( w / 2);
          const win = window.open(gmailAuthUrl, 'gmail-oauth', `width=${w},height=${h},left=${x},top=${y},resizable=yes,scrollbars=yes,status=yes`);
          if (!win || win.closed || typeof win.closed === 'undefined') {
            window.location.href = gmailAuthUrl;
            return;
          }
          try { setConnectingJob(String(jobObj.jobId || jobObj.job_id || jobObj.id || jobObj.id)); } catch(_) {}
          const handler = (ev) => {
            try {
              if (ev && ev.data && ev.data.type === 'gmail_connected') {
                window.removeEventListener('message', handler);
                setGmailGranted(true);
                const jid = String(jobObj.jobId || jobObj.job_id || jobObj.id);
                setGmailJustConnectedJob(jid);
                setConnectingJob(null);
                setTimeout(() => { try { setGmailJustConnectedJob(null); } catch(_) {} }, 700);
                startRetry(jid, manualEmail, true);
              }
            } catch (err) { setConnectingJob(null); }
          };
          window.addEventListener('message', handler);
          return;
        }
      }
    } catch (err) {
      try { setConnectingJob(null); } catch(_) {}
      console.error('[CompletedApplications] ensureGmailThenStart permission-check error', { message: err?.message, code: err?.code, status: err?.response?.status, data: err?.response?.data });
      if (err && err.code === 'ECONNABORTED') {
        try { errorSetter('Gmail permission check timed out. Try again.'); } catch(_) {}
        return;
      }
    }
    // proceed
    try { setConnectingJob(null); } catch(_) {}
    console.debug('[CompletedApplications] ensureGmailThenStart proceeding to startRetry', { jobId: String(jobObj.jobId || jobObj.job_id || jobObj.id), manualEmailProvided: !!manualEmail });
    startRetry(String(jobObj.jobId || jobObj.job_id || jobObj.id), manualEmail, true);
  };

  return (
    <div className="auth-container" style={{ paddingTop: 16 }}>
      <AuthSideMenu
        opened={authMenuOpen}
        hidden={!authMenuOpen}
        resumeSubDuration={''}
        isResumeSubbed={false}
        arrayDetails={[]}
      />
      <div style={{ width: '95%', margin: '0 auto', paddingTop: 12 }}>
        <AuthHeader
          authMenuOpen={authMenuOpen}
          onClick={toggleMenu}
          headerText="Applications"
        />
        <JobViewStyled theme={themeName} style={{minHeight: '60vh'}}>
          <OverflowContainerStyled style={{ flex: 1, minHeight: 0, paddingTop: headerPad }}>
            <Button
                secondaryLight={true}
                link='/user/dashboard/job-hub'
            >
                <FaLongArrowAltLeft size='1rem' color='crimson' /> &nbsp;to job hub
            </Button>  
            <div ref={headerRef}>
              <PageHeaderStyled theme={themeName}>Applications</PageHeaderStyled>
            </div>
            <RuleStyled />
            <div style={{ marginTop: 4, fontSize: '.85rem', opacity: 0.9, color: isDark ? '#fff' : '#333' }}>
              Tip: Check your registered email account (Sent, Inbox or Junk) to view each sent application status, including the attached resume and cover letter. For failed applications, retry or use the AI-Assisted Apply button per job in the job hub to apply semi-assistedly.
            </div>
            <div style={{ padding: '6px 0 8px 0' }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', borderRadius: 8, boxShadow: 'inset 0 -2px 0 rgba(0,0,0,0.03)', border: '1px solid rgba(0,0,0,0.06)', background: isDark ? '#121212' : '#fff' }}>
                    <FaClipboardList color="#5fbec5" />
                    <div style={{ fontSize: '.8rem', color: isDark ? 'yellow' : '#5fbec5' }}>
                      <div style={{ fontSize: '.7rem', opacity: .8 }}>Total Applications</div>
                      <strong>{totalCount !== null ? `${totalCount}` : items.length}</strong>
                    </div>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', borderRadius: 8, boxShadow: 'inset 0 -2px 0 rgba(0,0,0,0.03)', border: '1px solid rgba(0,0,0,0.06)', background: isDark ? '#121212' : '#fff' }}>
                    <FaCalendarAlt color="#5fbec5" />
                    <div style={{ fontSize: '.8rem', color: isDark ? '#fff' : '#333' }}>
                      <div style={{ fontSize: '.7rem', opacity: .8 }}>This Month</div>
                      <strong>{totalThisMonth !== null ? String(totalThisMonth) : '-'}</strong>
                    </div>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', borderRadius: 8, boxShadow: 'inset 0 -2px 0 rgba(0,0,0,0.03)', border: '1px solid rgba(0,0,0,0.06)', background: isDark ? '#121212' : '#fff' }}>
                    <FaEye color="#5fbec5" />
                      <div style={{ fontSize: '.8rem', color: isDark ? '#fff' : '#333' }}>
                      <div style={{ fontSize: '.7rem', opacity: .8 }}>Showing</div>
                      <strong>{items.length}{totalCount && totalCount > items.length ? `/${totalCount}` : ''}</strong>
                    </div>
                  </div>
                </div>
                {gmailIsGmail && (
                  <div style={{ display:'inline-flex', alignItems:'center', gap:8 }}>
                    {gmailGranted ? (
                      <span title="Gmail connected" style={{ display:'inline-flex', alignItems:'center', gap:6, border:'1px dashed #5fbec5', color:'#5fbec5', padding:'2px 8px', borderRadius: '999px', fontSize:'.75rem' }}>
                        <SiGmail size={14} /> Connected
                        <button onClick={!disconnectPending ? handleDisconnectGmail : undefined} style={{ marginLeft:8, border:'none', background:'transparent', color:'#EA4335', cursor: disconnectPending ? 'wait' : 'pointer', fontSize:'.75rem', textDecoration: disconnectPending ? 'none' : 'underline', display:'inline-flex', alignItems:'center', gap:6 }}>
                          {disconnectPending ? (<OvalSpinner size={12} color="#EA4335" />) : 'Disconnect'}
                        </button>
                      </span>
                    ) : (
                      <span title="Gmail not connected" style={{ display:'inline-flex', alignItems:'center', gap:6, border:'1px dashed #999', color:'#666', padding:'2px 8px', borderRadius: '999px', fontSize:'.75rem' }}>
                        <SiGmail size={14} /> Not connected
                        <button onClick={async () => {
                          try {
                            const token = localStorage.getItem('token');
                            let gmailUrl = gmailAuthUrl;
                            if (!gmailUrl) {
                              const s = await axios.get('/user/gmail/oauth/start?mode=popup', { headers: { 'x-access-token': token }});
                              gmailUrl = s?.data?.authUrl;
                            }
                            if (gmailUrl) {
                              const w = 720, h = 700;
                              const x = Math.max(0, Math.floor((window.screen.availWidth - w) / 2));
                              const y = Math.max(0, Math.floor((window.screen.availHeight - h) / 2));
                              const win = window.open(gmailUrl, 'gmail-oauth', `width=${w},height=${h},left=${x},top=${y},resizable=yes,scrollbars=yes,status=yes`);
                              try { gmailPopupRef.current = win; } catch(_) {}
                              if (!win) window.location.href = gmailUrl;
                              try {
                                if (gmailPopupCheckRef.current) clearInterval(gmailPopupCheckRef.current);
                                gmailPopupCheckRef.current = setInterval(async () => {
                                  try {
                                    if (!gmailPopupRef.current || gmailPopupRef.current.closed) {
                                      clearInterval(gmailPopupCheckRef.current);
                                      gmailPopupCheckRef.current = null;
                                      const tk = localStorage.getItem('token');
                                      const r = await axios.get('/user/gmail/permission-status', { headers: { 'x-access-token': tk }});
                                      const granted = !!r?.data?.granted;
                                      if (granted) {
                                        setGmailGranted(true);
                                        setGmailIsGmail(!!r?.data?.isGmail);
                                        try { successSetter('Gmail connected'); } catch(_) {}
                                      }
                                    }
                                  } catch(_) {}
                                }, 800);
                              } catch(_) {}
                            } else {
                              window.location.href = '/user/dashboard/auto-apply';
                            }
                          } catch (e) {
                            try { window.location.href = '/user/dashboard/auto-apply'; } catch(_){ }
                          }
                        }} style={{ marginLeft:8, border:'none', background:'transparent', color:'#3E8F93', cursor: 'pointer', fontSize:'.75rem', textDecoration: 'underline', display:'inline-flex', alignItems:'center', gap:6 }}>
                          Connect
                        </button>
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>
            {localLoading ? (
              <div style={{ padding: '12px 0', display: 'flex', flexDirection: 'column', gap: 10 }}>
                {Array.from({ length: 5 }).map((_, i) => (
                  <Card key={`skeleton-${i}`} sx={{ backgroundColor: 'transparent', borderRadius: '12px', border: '1px solid #c0d1d457', position: 'relative', paddingBottom: 0 }}>
                    <CardContent style={{ display: 'flex', alignItems: isDesktop ? 'center' : 'flex-start', gap: isDesktop ? 5 : 3, flexWrap: isDesktop ? 'nowrap' : 'wrap' }}>
                      <ImageAltStyled theme={themeName}>
                        <div style={{ width: 48, height: 48, borderRadius: 6, background: isDark ? '#2a2a2a' : '#e8eef0' }} />
                      </ImageAltStyled>
                      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
                        <div style={{ width: '40%', height: 14, borderRadius: 4, background: isDark ? '#2a2a2a' : '#e8eef0' }} />
                        <div style={{ width: '30%', height: 12, borderRadius: 4, background: isDark ? '#2a2a2a' : '#e8eef0' }} />
                        <div style={{ width: '20%', height: 10, borderRadius: 4, background: isDark ? '#2a2a2a' : '#e8eef0' }} />
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : items.length === 0 ? (
              <div style={{ padding: '10px 0', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: isDark ? '#fff' : '#333' }}>
                <div style={{ marginBottom: 10, fontSize: '.95rem', textAlign: 'center' }}>No applications yet. Start applying from your Job Connection Hub.</div>
                <div style={{ maxWidth: 320 }}>
                  <Button width="100%" primary={true} link={'/user/dashboard/job-hub'}>
                    Go to JobHub
                  </Button>
                </div>
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 10 }}>
                {items.map((it, idx) => {
                  const idStr = keyForItem(it, idx);
                  const isSuccess = it.status === 'success';
                  const isFailed = it.status === 'failed';
                  const appliedDate = it.appliedAt ? new Date(it.appliedAt) : null;
                  const emailStr = (it.recruiterEmails || []).slice(0,2).join(', ');
                  const showRetry = isFailed;
                  const isActiveRetry = retryingJob && String(retryingJob) === String(it.jobId || it.job_id || it.id);
                  return (
                    <Card key={idStr} ref={el => { if (el) itemRefs.current[idStr] = el; else delete itemRefs.current[idStr]; }} sx={{ backgroundColor: 'transparent', borderRadius: '12px', border: '1px solid #c0d1d457', position: 'relative', paddingBottom: showRetry ? '48px' : 0 }}>
                      <CardContent style={{ display: 'flex', alignItems: isDesktop ? 'center' : 'flex-start', gap: isDesktop ? 5 : 3, flexWrap: isDesktop ? 'nowrap' : 'wrap' }}>
                        <ImageAltStyled theme={themeName}>
                          <h1>{(it.company || 'C').charAt(0)}</h1>
                        </ImageAltStyled>
                        <div style={{ flex: 1, minWidth: 0, color: isDark ? '#fff' : 'inherit', display: 'flex', flexDirection: 'column', gap: isDesktop ? 0 : 2 }}>
                          <div style={{ fontWeight: 600, lineHeight: 1.2 }}>{it.title}</div>
                          <div style={{ opacity: 0.8, fontSize: '.85rem' }}>{it.company}</div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginTop: 6 }}>
                            <span style={{ fontSize: '.8rem', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                              {isSuccess && <FaCheckCircle color="#41a956" />} {isFailed && <FaTimesCircle color="crimson" />} {it.status}
                            </span>
                            {appliedDate && (
                              <span style={{ fontSize: '.8rem', opacity: 0.85 }}>{appliedDate.toLocaleDateString()}</span>
                            )}
                            {emailStr && (
                              <span style={{ fontSize: '.8rem', display: 'inline-flex', alignItems: 'center', gap: 6, opacity: 0.9 }}>
                                <FaEnvelope /> {emailStr}
                              </span>
                            )}
                            {it.method && (
                              <span style={{ fontSize: '.8rem', opacity: 0.85 }}>{it.method}</span>
                            )}
                            {isFailed && it.reason && (
                              <span style={{ fontSize: '.8rem', opacity: 0.85, color: isDark ? '#ffb3b3' : '#ba1a1a' }}>reason: {it.reason}</span>
                            )}
                          </div>
                        </div>
                      </CardContent>
                      {showRetry && (
                        <div style={{ position: 'absolute', right: 10, bottom: 10 }}>
                          {isActiveRetry ? (
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: '.8rem', color: isDark ? '#fff' : '#333' }}>
                              <OvalSpinner size={14} color="#5fbec5" /> {progressText} {progressCounts.done}/{progressCounts.total || 1}
                            </span>
                          ) : (() => {
                            const jobIdStr = String(it.jobId || it.job_id || it.id || '');
                            if (connectingJob && String(connectingJob) === jobIdStr) {
                              return (
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: '.8rem', color: isDark ? '#fff' : '#333' }}>
                                  <OvalSpinner size={14} color="#5fbec5" /> Connecting Gmail
                                </span>
                              );
                            }
                            if (gmailJustConnectedJob && String(gmailJustConnectedJob) === jobIdStr) {
                              return (
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: '.8rem', color: isDark ? '#fff' : '#333' }}>
                                  <OvalSpinner size={14} color="#5fbec5" /> Gmail connected
                                </span>
                              );
                            }
                            return (
                              <StartPillButton
                                onClick={async () => {
                                  if (retryingJob) return;
                                  const failureIsNoEmail = String(it.reason || '').toLowerCase().includes('no_company_email_found') || String(it.reason || '').toLowerCase().includes('lack of email');
                                  const jobId = String(it.jobId || it.job_id || it.id || '');
                                  if (!jobId) return;
                                  if (failureIsNoEmail) {
                                    setEmailForJob({ jobId, reason: it.reason, cardKey: idStr });
                                    setEmailInput('');
                                    setEmailModalOpen(true);
                                    return;
                                  }
                                  try {
                                    await confirm({ title: 'Try again?', description: 'We will re-run the auto-apply flow for this job.' });
                                  } catch { return; }
                                  // Animate move to top using stable key then start
                                  moveCardToTopAnimated(idStr);
                                  // show connecting state immediately while we check permission/start oauth
                                  try { setConnectingJob(jobId); } catch(_) {}
                                  ensureGmailThenStart({ jobId }, null);
                                }}
                                disabled={!!retryingJob}
                                pending={false}
                              >
                                Try Again
                              </StartPillButton>
                            );
                          })()
                          }
                        </div>
                      )}
                    </Card>
                  );
                })}
                {loadingMore && (
                  Array.from({ length: 3 }).map((_, i) => (
                    <Card key={`skeleton-more-${i}`} sx={{ backgroundColor: 'transparent', borderRadius: '12px', border: '1px solid #c0d1d457' }}>
                      <CardContent style={{ display: 'flex', alignItems: isDesktop ? 'center' : 'flex-start', gap: isDesktop ? 5 : 3 }}>
                        <ImageAltStyled theme={themeName}><div style={{ width: 48, height: 48, borderRadius: 6, background: isDark ? '#2a2a2a' : '#e8eef0' }} /></ImageAltStyled>
                        <div style={{ flex: 1 }}>
                          <div style={{ width: '40%', height: 14, borderRadius: 4, background: isDark ? '#2a2a2a' : '#e8eef0', marginBottom: 8 }} />
                          <div style={{ width: '30%', height: 12, borderRadius: 4, background: isDark ? '#2a2a2a' : '#e8eef0' }} />
                        </div>
                      </CardContent>
                    </Card>
                  ))
                )}
                <div ref={bottomRef} style={{ height: 1 }} />
              </div>
            )}
          </OverflowContainerStyled>
        </JobViewStyled>
      </div>
      {emailModalOpen && (
        <PlainModalOverlay>
          <div style={{ width: '100%', textAlign: 'center' }}>
            <div className='prev-page' onClick={() => { setEmailModalOpen(false); setEmailForJob(null); }}>
              <FaLongArrowAltLeft />
            </div>
            <h4 style={{ color: '#3E8F93', marginBottom: 10 }}>No recruiter found</h4>
            <p style={{ fontSize: '.9rem', opacity: .9, margin: '6px auto', maxWidth: 520 }}>This recruiter prevented email Fetching or email not available. Use query below on Google search to get the email if available.</p>
            {(() => {
              try {
                const jid = String(emailForJob?.jobId || '');
                const match = items.find(it => String(it?.jobId || it?.job_id || it?.id || '') === jid);
                const company = (match?.company_domain || match?.company_object?.domain || match?.company || '').trim() || 'the company';
                const query = `${company} career or contact email`;
                return (
                  <Alert
                    sx={{
                      mb: 1,
                      fontSize: '.85rem',
                      textAlign: 'left',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8
                    }}
                    severity="warning"
                    role="button"
                    title="Tap to copy"
                    onClick={async () => {
                      try {
                        await copyText(query);
                        try { successSetter('Copied'); } catch {}
                      } catch {
                        try { errorSetter('Failed to copy'); } catch {}
                      }
                    }}
                  >
                    <strong>{query}.</strong> &nbsp; &nbsp;
                    <FaRegCopy size={14} style={{ flex: '0 0 auto' }} />
                  </Alert>
                );
              } catch {
                return null;
              }
            })()}
            <div style={{ width: '100%', marginTop: 8 }}>
              <AuthInput
                id="manualEmail"
                name="manualEmail"
                value={emailInput}
                label="Recruiter or careers email (optional, 2 max, comma separated)"
                inputType="email"
                inputGridSm={12}
                onChange={(e) => setEmailInput(e.target.value)}
                mb={2}
              />
            </div>
              <div style={{ width: '100%', marginTop: 12 }}>
              <Button width="100%" primary={true} clickHandle={() => {
                if (!emailForJob) { setEmailModalOpen(false); return; }
                const raw = (emailInput || '').trim();
                const { valid, invalid } = parseAndValidateEmails(raw);
                if (invalid && invalid.length) { try { errorSetter(`Invalid email(s): ${invalid.join(', ')}`); } catch(_) {} return; }
                if (raw && (!valid || valid.length === 0)) { try { errorSetter('No valid email found'); } catch(_) {} return; }
                setEmailModalOpen(false);
                try { moveCardToTopAnimated(String(emailForJob.cardKey || emailForJob.jobId)); } catch {}
                try { setConnectingJob(String(emailForJob.jobId)); } catch(_) {}
                ensureGmailThenStart({ jobId: emailForJob.jobId }, valid.length ? valid : null);
              }}>
                Start
              </Button>
            </div>
          </div>
        </PlainModalOverlay>
      )}
      {showEmailPermModal && (
        <PlainModalOverlay>
          <div style={{ width: '100%', textAlign: 'center' }}>
            <div className='prev-page' onClick={() => { setShowEmailPermModal(false); setPendingGmailJobObj(null); }}>
              <FaLongArrowAltLeft />
            </div>
            <h4 style={{ color: '#3E8F93', marginBottom: 10 }}>Allow Bubble Ai to send emails on your behalf</h4>
            <Alert sx={{padding: '0 5px', fontSize: '.9rem', marginBottom: 8}} severity="info">
              We'll send follow-up or application emails from your Gmail account only when you start Auto-Apply. You can revoke access anytime from your Google account.
            </Alert>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'center', marginTop: 8 }}>
              <SiGmail color="#EA4335" size={28} />
            </div>
            <div style={{ width: '100%', marginTop: 20 }}>
              <ButtonSubmitGreen onClick={async () => {
                // Connect Gmail and continue
                const pj = pendingGmailJobObj;
                if (!pj) { setShowEmailPermModal(false); return; }
                const isAuth = localStorage.getItem('token');
                try {
                  if (!gmailAuthUrl) {
                    const s = await axios.get('/user/gmail/oauth/start?mode=popup', { headers: { 'x-access-token': isAuth }});
                    if (s?.data?.authUrl) setGmailAuthUrl(s.data.authUrl);
                  }
                } catch (e) { /* ignore */ }
                if (!gmailAuthUrl) { try { errorSetter('Unable to start Gmail connection. Try again.'); } catch(_) {} return; }
                const w = 520, h = 640;
                const y = window.top.outerHeight / 2 + window.top.screenY - ( h / 2);
                const x = window.top.outerWidth / 2 + window.top.screenX - ( w / 2);
                const win = window.open(gmailAuthUrl, 'gmail-oauth', `width=${w},height=${h},left=${x},top=${y},resizable=yes,scrollbars=yes,status=yes`);
                gmailPopupRef.current = win;
                if (!win || win.closed || typeof win.closed === 'undefined') {
                  window.location.href = gmailAuthUrl;
                  return;
                }
                try { setConnectingJob(String(pj.jobId || pj.job_id || pj.id)); } catch(_) {}
                const handler = (ev) => {
                  try {
                    if (ev && ev.data && ev.data.type === 'gmail_connected') {
                      window.removeEventListener('message', handler);
                      try { if (gmailPopupCheckRef.current) { clearInterval(gmailPopupCheckRef.current); gmailPopupCheckRef.current = null; } } catch(_) {}
                      gmailPopupRef.current = null;
                      setShowEmailPermModal(false);
                      setPendingGmailJobObj(null);
                      setGmailGranted(true);
                      setGmailIsGmail(true);
                      const jid = String(pj.jobId || pj.job_id || pj.id);
                      setGmailJustConnectedJob(jid);
                      setConnectingJob(null);
                      setTimeout(() => { try { setGmailJustConnectedJob(null); } catch(_) {} }, 700);
                      startRetry(jid, null, true);
                    }
                  } catch(_) { setConnectingJob(null); }
                };
                window.addEventListener('message', handler);
                try {
                  if (gmailPopupCheckRef.current) { clearInterval(gmailPopupCheckRef.current); }
                  gmailPopupCheckRef.current = setInterval(async () => {
                    try {
                      if (!gmailPopupRef.current || gmailPopupRef.current.closed) {
                        clearInterval(gmailPopupCheckRef.current);
                        gmailPopupCheckRef.current = null;
                        const r = await axios.get('/user/gmail/permission-status', { headers: { 'x-access-token': isAuth }});
                        const granted = !!r?.data?.granted;
                        if (granted) {
                          setGmailGranted(true);
                          setGmailIsGmail(!!r?.data?.isGmail);
                          setShowEmailPermModal(false);
                          const jid = String(pj.jobId || pj.job_id || pj.id);
                          startRetry(jid, null, true);
                        }
                      }
                    } catch(_){}
                  }, 800);
                } catch(_){}
              }} style={{ width: '100%', maxWidth: 320, background: '#3E8F93' }}>
                Connect Gmail and Continue
              </ButtonSubmitGreen>
              <div style={{ height: 10 }} />
              <Alert sx={{padding: '5px', fontSize: '.8rem', marginBottom: 12}} severity="warning">
                Optionally proceed without connecting your Gmail BUT sending will be done from Bubble Ai's internal sender which might be less effective.
              </Alert>
              <div style={{ marginTop: '-70px' }}>
                <Button width="100%" secondaryLight={true} clickHandle={() => {
                  const pj = pendingGmailJobObj;
                  setShowEmailPermModal(false);
                  setPendingGmailJobObj(null);
                  if (pj) startRetry(String(pj.jobId || pj.job_id || pj.id), null, true);
                }}>
                  Continue Without
                </Button>
              </div>
            </div>
          </div>
        </PlainModalOverlay>
      )}
    </div>
  );
};

export default CompletedApplications;
